import React, { createContext, useCallback, useEffect } from 'react'
import { getGivenFeedback as getGivenFeedbackApi } from '@verzekeringapp-mono/shared/src/api/feedback'
import { useEmergencyModalContext } from './EmergencyModalProvider'
import { useLocation } from 'react-router-dom'
import FeedbackModal from '~/components/FeedbackModal'
import FeedbackResponseModal from '~/components/FeedbackResponseModal'
import { getUser } from '@verzekeringapp-mono/shared/src/state/selectors/user'
import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import { useFeedbackModalScheduler } from '@verzekeringapp-mono/shared/src/hooks/use_feedback_modal_scheduler'

type FeedbackModalContextType = {
  // Nothing
};

const FeedbackModalContext = createContext<
  FeedbackModalContextType | undefined
>(undefined)

type Props = {
  children: React.ReactNode;
};

const excludedPaths = [
  '/schade-melden',
  '/schade-melden/schadeformulier',
  '/schadehersteller/voertuigschade',
  '/instellen',
  '/home'
]
const excludedPathPrefixes = [
  '/dossier/waardemeters/herbouwwaardemeter',
  '/dossier/waardemeters/inboedelwaardemeter'
]
const pausedPathPrefixes = ['/instellen']

const timeoutDuration = 1000 * 60 * 3 // 3 minutes
const routeNavigationDelay = 1000 * 2 // 2 seconds
const feedbackCooldown = 1000 * 10 // 10 seconds

function getIsPageActive () {
  if (typeof document === 'undefined') {
    return true
  }

  return document.visibilityState === 'visible' && document.hasFocus()
}

export const FeedbackModalProvider = ({ children }: Props) => {
  const location = useLocation()
  const path = location.pathname
  const [hasGivenFeedback, setHasGivenFeedback] = React.useState(false)
  const [isPageActive, setIsPageActive] = React.useState(getIsPageActive)
  const { isOpen: emergencyModalOpen } = useEmergencyModalContext()
  const [isShowingFeedbackModal, setIsShowingFeedbackModal] =
    React.useState(false)
  const [responseType, setResponseType] = React.useState<string | null>(null)
  const user = useAppSelector(getUser)

  const getGivenFeedback = useCallback(async () => {
    if (!user) {
      return
    }

    try {
      const {
        data: { showFeedbackForm }
      } = await getGivenFeedbackApi()

      setHasGivenFeedback(showFeedbackForm === false)
    } catch (error) {
      // Handle error if needed
    }
  }, [user])

  useEffect(() => {
    getGivenFeedback()
  }, [getGivenFeedback])

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return
    }

    const updatePageActivity = () => {
      setIsPageActive(getIsPageActive())
    }

    updatePageActivity()

    document.addEventListener('visibilitychange', updatePageActivity)
    window.addEventListener('focus', updatePageActivity)
    window.addEventListener('blur', updatePageActivity)

    return () => {
      document.removeEventListener('visibilitychange', updatePageActivity)
      window.removeEventListener('focus', updatePageActivity)
      window.removeEventListener('blur', updatePageActivity)
    }
  }, [])

  useFeedbackModalScheduler({
    path,
    excludedPaths,
    excludedPathPrefixes,
    initialDelayMs: timeoutDuration,
    pausedPathPrefixes,
    paused: !isPageActive,
    routeDelayMs: routeNavigationDelay,
    cooldownMs: feedbackCooldown,
    shouldPrompt: Boolean(user) && !hasGivenFeedback,
    blocked:
      emergencyModalOpen || isShowingFeedbackModal || responseType !== null,
    onTrigger: () => {
      setIsShowingFeedbackModal(true)
    }
  })

  return (
    <FeedbackModalContext.Provider value={undefined}>
      {children}

      {isShowingFeedbackModal && (
        <FeedbackModal
          onFeedbackSend={responseType => {
            setIsShowingFeedbackModal(false)
            setResponseType(responseType)

            setHasGivenFeedback(true)
          }}
          onCancel={() => {
            setIsShowingFeedbackModal(false)
            setHasGivenFeedback(true)
          }}
        />
      )}

      {responseType && (
        <FeedbackResponseModal
          responseType={responseType}
          onCancel={() => setResponseType(null)}
        />
      )}
    </FeedbackModalContext.Provider>
  )
}
